import React, {PureComponent} from "react";
import {
  ICaptionOps,
  moveLine,
  newEditorOps,
  newValueOps,
  setLineBeginning,
  setLineEnd,
  setWordData,
} from "../../models/Captions";
import * as PIXI from 'pixi.js'
import {Container, Text, withPixiApp} from '@pixi/react'
import {string2hex} from "./utils";
import {TextStyle} from 'pixi.js';
import {Scale} from "./coordinates";
import {ICaptionsEditor} from "../../models/ICaptionsEditor";
import {Rectangle} from "./Reactangle";
import {CaptionTrackRoot} from "../../models/Captions/types";
import {isEmpty} from "../../utils";
import {getText} from "../../models/Captions/slateApi";
import {getWordTimingLayout, StagedWordTiming, WordTimingLayout} from "./wordTiming";


type Props = {
  offsetSeconds: number,
  maxSeconds: number,
  captions: CaptionTrackRoot,
  scale: Scale,
  captionsEditor: ICaptionsEditor,
  onLineSelect: (lineIdx: number) => void,
  onLineDoubleClick?: (lineIdx: number) => void,
  onWordSelect?: (vocableId: string) => void,
  selectedLine: number,
  selectedWordVocableId?: string|null,
  wordTimingLineIdx?: number|null,
};


type Move = {
  mode: 'end'|'start'|'move',
  seconds: number
};

const STAGED_WORD_Y = 4;
const MAIN_WORD_Y = 30;
const STAGED_WORD_START_X = 8;
const STAGED_WORD_GAP = 8;


export function CaptionOverlay(props: Props) {
  if (props.wordTimingLineIdx !== null && props.wordTimingLineIdx !== undefined) {
    return <WordCaptionOverlay {...props} />;
  }

  return <LineCaptionOverlay {...props} />;
}


class LineCaptionOverlay extends PureComponent<Props> {
  render() {
    const { offsetSeconds, maxSeconds } = this.props;

    let items: React.ReactNode[] = [];

    const visibleLines = Array.from(getVisibleLines(this.props.captions, offsetSeconds, maxSeconds));
    visibleLines.forEach((line) => {
      const x = this.props.scale.secondsToPixel(line.time - offsetSeconds)
      const width = this.props.scale.secondsToPixel(line.duration || .5);
      const text = getText(line.line);

      items.push(<Vocable
        text={text}
        x={x}
        width={width}
        key={line.index}
        itemId={line.index}
        onMove={this.handleMove}
        onMoveDone={this.handleMoveDone}
        onSelect={this.props.onLineSelect}
        onDoubleClick={this.props.onLineDoubleClick}
        isSelected={this.props.selectedLine === line.index}
      />);
    })

    return items;
  }

  lastMove: Move|null = null;

  handleMove = (lineIdx: number, pixelPos: number, mode: 'end'|'start'|'move') => {
    const posAsSeconds = this.props.scale.pixelsToTime(pixelPos);
    const newSeconds = roundToHundredths(this.props.offsetSeconds + posAsSeconds);
    this.lastMove = {
      seconds: newSeconds,
      mode: mode
    };

    const ops = newValueOps(this.props.captionsEditor.committedValue, {
      idGen: this.props.captionsEditor.slateEditor.keygen,
    });
    applyLineMove(this.lastMove, ops, lineIdx);
    this.props.captionsEditor.setShadowValue(ops.value);
  }

  handleMoveDone = (lineIdx: number) => {
    const lastMove = this.lastMove;
    if (lastMove === null) {
      return;
    }

    this.props.captionsEditor.setShadowValue(null);
    const ops = newEditorOps(this.props.captionsEditor);
    applyLineMove(lastMove, ops, lineIdx);
  }
}


class WordCaptionOverlay extends PureComponent<Props> {
  render() {
    const lineIdx = this.props.wordTimingLineIdx;
    if (lineIdx === null || lineIdx === undefined) {
      return null;
    }

    const layout = getWordTimingLayout(this.props.captions, lineIdx);
    if (!layout) {
      return null;
    }

    const items: React.ReactNode[] = [];
    let stagedX = STAGED_WORD_START_X;

    layout.stagedWords.forEach((word) => {
      const width = estimateStagedWordWidth(word.text);

      items.push(<Vocable
        text={word.text}
        x={stagedX}
        y={STAGED_WORD_Y}
        width={width}
        key={word.vocableId}
        itemId={word.vocableId}
        onMove={this.handleMove}
        onMoveDone={this.handleMoveDone}
        onClick={this.props.onWordSelect}
        isSelected={this.props.selectedWordVocableId === word.vocableId}
        fillColor={word.canPlace ? '#eceff1' : '#cfd8dc'}
        borderColor={word.canPlace ? '#90a4ae' : '#b0bec5'}
        disableLeftResize={true}
        disableRightResize={true}
      />);

      stagedX += width + STAGED_WORD_GAP;
    });

    layout.placedWords.forEach((word) => {
      const x = this.props.scale.secondsToPixel(word.absoluteTime - this.props.offsetSeconds);
      const width = this.props.scale.secondsToPixel(word.absoluteDuration || .25);
      const boundaryX = this.props.scale.secondsToPixel(
        (word.absoluteTime + word.absoluteDuration) - this.props.offsetSeconds
      );

      items.push(<Vocable
        text={word.text}
        x={x}
        y={MAIN_WORD_Y}
        width={width}
        key={word.vocableId}
        itemId={word.vocableId}
        onMove={this.handleMove}
        onMoveDone={this.handleMoveDone}
        onClick={this.props.onWordSelect}
        isSelected={this.props.selectedWordVocableId === word.vocableId}
        fillColor={'#fff3e0'}
        borderColor={'#fb8c00'}
        disableLeftResize={word.isLinkedToPrevious}
        disableRightResize={word.isLinkedToNext}
      />);

      if (word.isLinkedToNext) {
        items.push(<LinkedBoundaryMarker
          key={`linked-boundary-${word.vocableId}`}
          x={boundaryX}
          itemId={word.vocableId}
          onMove={this.handleMove}
          onMoveDone={this.handleMoveDone}
          onDoubleClick={this.handleLinkedBoundarySeparate}
        />);
      }
    });

    return items;
  }

  lastMove: Move|null = null;

  handleMove = (vocableId: string, pixelPos: number, mode: 'end'|'start'|'move') => {
    const lineIdx = this.props.wordTimingLineIdx;
    if (lineIdx === null || lineIdx === undefined) {
      return;
    }

    const posAsSeconds = this.props.scale.pixelsToTime(pixelPos);
    const newSeconds = roundToHundredths(this.props.offsetSeconds + posAsSeconds);
    this.lastMove = {
      seconds: newSeconds,
      mode: mode
    };

    const ops = newValueOps(this.props.captionsEditor.committedValue, {
      idGen: this.props.captionsEditor.slateEditor.keygen,
    });
    const didApply = applyWordMove(this.lastMove, ops, lineIdx, vocableId);
    this.props.captionsEditor.setShadowValue(didApply ? ops.value : null);
  }

  handleMoveDone = (vocableId: string) => {
    const lineIdx = this.props.wordTimingLineIdx;
    const lastMove = this.lastMove;
    if (lineIdx === null || lineIdx === undefined || lastMove === null) {
      return;
    }

    this.props.captionsEditor.setShadowValue(null);
    const ops = newEditorOps(this.props.captionsEditor);
    applyWordMove(lastMove, ops, lineIdx, vocableId);
  }

  handleLinkedBoundarySeparate = (vocableId: string) => {
    const lineIdx = this.props.wordTimingLineIdx;
    if (lineIdx === null || lineIdx === undefined) {
      return;
    }

    this.props.captionsEditor.setShadowValue(null);
    const ops = newEditorOps(this.props.captionsEditor);
    separateLinkedBoundary(ops, lineIdx, vocableId);
  }
}


function applyLineMove(move: Move, ops: ICaptionOps, idx: number) {
  if (move.mode === 'end') {
    setLineEnd(ops, idx, move.seconds);
  }
  else if (move.mode === 'start') {
    setLineBeginning(ops, idx, move.seconds);
  }
  else {
    moveLine(ops, idx, move.seconds);
  }
}


function applyWordMove(move: Move, ops: ICaptionOps, lineIdx: number, vocableId: string) {
  const layout = getWordTimingLayout(ops.value, lineIdx);
  if (!layout) {
    return false;
  }

  const placedWordIndex = layout.placedWords.findIndex(candidate => candidate.vocableId === vocableId);
  if (placedWordIndex !== -1) {
    const word = layout.placedWords[placedWordIndex];
    const previousWord = placedWordIndex > 0 ? layout.placedWords[placedWordIndex - 1] : null;
    const nextWord = placedWordIndex < layout.placedWords.length - 1 ? layout.placedWords[placedWordIndex + 1] : null;

    const previousWordEnd = previousWord
      ? previousWord.relativeTime + previousWord.relativeDuration
      : 0;
    const nextWordStart = nextWord
      ? nextWord.relativeTime
      : layout.lineDuration;

    const relativeTime = clamp(move.seconds - layout.lineTime, 0, layout.lineDuration);

    if (move.mode === 'move') {
      const duration = clamp(word.relativeDuration, layout.minimumWordDuration, layout.lineDuration);
      const nextStart = clamp(
        relativeTime,
        previousWordEnd,
        Math.max(previousWordEnd, nextWordStart - duration)
      );
      setWordData(ops, word.node, word.path, {
        time: nextStart,
        duration,
      });
      return true;
    }

    if (move.mode === 'start') {
      applyWordStartResize(ops, layout.placedWords, placedWordIndex, relativeTime, layout.minimumWordDuration);
      return true;
    }

    applyWordEndResize(ops, layout.placedWords, placedWordIndex, relativeTime, layout.minimumWordDuration, layout.lineDuration);
    return true;
  }

  const stagedWord = layout.stagedWords.find(candidate => candidate.vocableId === vocableId);
  if (!stagedWord || move.mode !== 'move') {
    return false;
  }

  return applyStagedWordPlacement(ops, layout, stagedWord, move.seconds);
}


function applyWordStartResize(
  ops: ICaptionOps,
  wordTimings: WordTimingLayout['placedWords'],
  wordIndex: number,
  relativeTime: number,
  minimumDuration: number,
) {
  const word = wordTimings[wordIndex];
  const previousWord = wordIndex > 0 ? wordTimings[wordIndex - 1] : null;
  const previousWordEnd = previousWord
    ? previousWord.relativeTime + previousWord.relativeDuration
    : 0;
  const currentEnd = word.relativeTime + word.relativeDuration;
  const nextStart = clamp(
    relativeTime,
    previousWordEnd,
    Math.max(previousWordEnd, currentEnd - minimumDuration)
  );

  if (word.hasExplicitDuration) {
    setWordData(ops, word.node, word.path, {
      time: nextStart,
      duration: Math.max(minimumDuration, currentEnd - nextStart),
    });
    return;
  }

  setWordData(ops, word.node, word.path, {
    time: nextStart,
  });
}


function applyWordEndResize(
  ops: ICaptionOps,
  wordTimings: WordTimingLayout['placedWords'],
  wordIndex: number,
  relativeTime: number,
  minimumDuration: number,
  lineDuration: number,
) {
  const word = wordTimings[wordIndex];
  const nextWord = wordIndex < wordTimings.length - 1 ? wordTimings[wordIndex + 1] : null;

  if (word.isLinkedToNext && nextWord) {
    applyWordStartResize(ops, wordTimings, wordIndex + 1, relativeTime, minimumDuration);
    return;
  }

  const currentStart = word.relativeTime;
  const maxEnd = nextWord ? nextWord.relativeTime : lineDuration;
  const nextEnd = clamp(
    relativeTime,
    currentStart + minimumDuration,
    maxEnd
  );

  setWordData(ops, word.node, word.path, {
    ...(word.hasExplicitTime ? {} : {time: currentStart}),
    duration: Math.max(minimumDuration, nextEnd - currentStart),
  });
}


function applyStagedWordPlacement(
  ops: ICaptionOps,
  layout: WordTimingLayout,
  stagedWord: StagedWordTiming,
  absoluteSeconds: number,
) {
  // Staged words do not reserve space. The user must create a real gap first,
  // and then place the next word in text order into that gap.
  if (!stagedWord.canPlace) {
    return false;
  }

  const previousPlacedWord = stagedWord.previousPlacedWordIndex === null
    ? null
    : layout.placedWords.find(word => word.wordIndex === stagedWord.previousPlacedWordIndex) || null;
  const nextPlacedWord = stagedWord.nextPlacedWordIndex === null
    ? null
    : layout.placedWords.find(word => word.wordIndex === stagedWord.nextPlacedWordIndex) || null;

  const minimumStart = previousPlacedWord
    ? previousPlacedWord.relativeTime + previousPlacedWord.relativeDuration
    : 0;
  const maximumEnd = nextPlacedWord
    ? nextPlacedWord.relativeTime
    : layout.lineDuration;

  if ((maximumEnd - minimumStart) < layout.minimumWordDuration) {
    return false;
  }

  const relativeTime = clamp(
    absoluteSeconds - layout.lineTime,
    minimumStart,
    maximumEnd - layout.minimumWordDuration,
  );

  setWordData(ops, stagedWord.node, stagedWord.path, {
    time: relativeTime,
    duration: layout.minimumWordDuration,
  });
  return true;
}


function separateLinkedBoundary(ops: ICaptionOps, lineIdx: number, leftVocableId: string) {
  const layout = getWordTimingLayout(ops.value, lineIdx);
  if (!layout) {
    return;
  }

  const word = layout.placedWords.find(candidate => candidate.vocableId === leftVocableId);
  if (!word || !word.isLinkedToNext) {
    return;
  }

  setWordData(ops, word.node, word.path, {
    ...(word.hasExplicitTime ? {} : {time: word.relativeTime}),
    duration: word.relativeDuration,
  });
}


function estimateStagedWordWidth(text: string) {
  return clamp(text.length * 8 + 16, 48, 140);
}


function roundToHundredths(value: number) {
  return parseFloat(value.toFixed(2));
}


function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}


const textStyle = new TextStyle({
  fontFamily: 'Arial',
  fontSize: 12,
  fill: '#222222'
});


const textStyleSelected = new TextStyle({
  fontFamily: 'Arial',
  fontSize: 12,
  fill: '#FFFFFF'
});


type VocableProps = {
  text: string,
  x: number,
  width: number,
  itemId: any,
  onMove: (wordId: any, time: number, mode: 'start'|'end'|'move') => void,
  onMoveDone: (wordId: any) => void,
  onSelect?: (wordId: any) => void,
  onClick?: (wordId: any) => void,
  onDoubleClick?: (wordId: any) => void,
  isSelected: boolean,
  y?: number,
  fillColor?: string,
  borderColor?: string,
  disableLeftResize?: boolean,
  disableRightResize?: boolean,
  app: PIXI.Application
}

export const Vocable = withPixiApp(
  // @ts-ignore
  class Vocable extends React.PureComponent<VocableProps> {

  state = {
    textToRender: ''
  };

  componentDidMount() {
    this.measure();

    this.props.app.renderer.plugins.interaction.cursorStyles['resizeLeft'] = 'col-resize';
    this.props.app.renderer.plugins.interaction.cursorStyles['resizeRight'] = 'col-resize';
  }

  componentDidUpdate(prevProps: VocableProps) {
    if (
      (prevProps.width !== this.props.width) ||
      (prevProps.text !== this.props.text)
    ) {
      this.measure();
    }
  }

  componentWillUnmount() {
    this.detachGlobalPointerListeners();
  }

  measure() {
    const measureStyle = textStyle.clone();
    measureStyle.wordWrap = true;
    measureStyle.wordWrapWidth = this.props.width;

    const { lines, lineWidths } = PIXI.TextMetrics.measureText(this.props.text, measureStyle);
    if (lineWidths[0] > this.props.width) {
      this.setState({textToRender: ""});
      return;
    }
    this.setState({textToRender: lines[0]})
  }

  render() {
    const fillColor = this.props.isSelected
      ? '#2196f3'
      : (this.props.fillColor || '#fbfcfd');
    const borderColor = this.props.isSelected
      ? '#fbfcfd'
      : (this.props.borderColor || '#2196f3');

    const bg = <Rectangle
      fill={string2hex(fillColor)}
      borderColor={string2hex(borderColor)}
      x={0}
      y={0}
      width={this.props.width}
      height={20}
    />

    const text = <Text
      x={2}
      y={2}
      text={this.state.textToRender}
      style={this.props.isSelected ? textStyleSelected : textStyle}
    />;

    return <Container
      x={this.props.x}
      y={this.props.y ?? 30}
      interactive={true}
      pointerdown={this.handlePointerDown}
      pointermove={this.handlePointerMove}
      pointerover={this.handlePointerOver}
      pointerout={this.handlePointerOut}
      pointerup={this.handlePointerUp}
      pointerupoutside={this.handlePointerUpOutside}
    >
      {bg}
      {text}
    </Container>
  }

  private isDown: boolean = false;
  private isOver: boolean = false;
  private resizeMode: 'left'|'right'|null = null;
  private isDragging: boolean = false;
  private dragStartXInside: number = 0;
  private dragEndXInside: number = 0;
  private dragStartStageX: number = 0;
  private dragStartStageY: number = 0;
  private lastPointerUpAt: number = 0;
  private hasGlobalPointerListeners: boolean = false;

  determineModeFromPointerEvent(e: any) {
    const pos = e.data.getLocalPosition(e.currentTarget);
    const fromLeft = pos.x;
    const fromRight = this.props.width - pos.x;

    if (fromLeft < 10 && !this.props.disableLeftResize) {
      return 'left';
    }
    else if (fromRight < 10 && !this.props.disableRightResize) {
      return 'right';
    }
    else {
      return null;
    }
  }

  attachGlobalPointerListeners() {
    if (this.hasGlobalPointerListeners) {
      return;
    }

    window.addEventListener('pointermove', this.handleGlobalPointerMove);
    window.addEventListener('pointerup', this.handleGlobalPointerUp);
    window.addEventListener('pointercancel', this.handleGlobalPointerUp);
    window.addEventListener('mousemove', this.handleGlobalPointerMove);
    window.addEventListener('mouseup', this.handleGlobalPointerUp);
    this.hasGlobalPointerListeners = true;
  }

  detachGlobalPointerListeners() {
    if (!this.hasGlobalPointerListeners) {
      return;
    }

    window.removeEventListener('pointermove', this.handleGlobalPointerMove);
    window.removeEventListener('pointerup', this.handleGlobalPointerUp);
    window.removeEventListener('pointercancel', this.handleGlobalPointerUp);
    window.removeEventListener('mousemove', this.handleGlobalPointerMove);
    window.removeEventListener('mouseup', this.handleGlobalPointerUp);
    this.hasGlobalPointerListeners = false;
  }

  getStagePositionFromGlobalPointerEvent(e: PointerEvent | MouseEvent) {
    const rect = (this.props.app.view as HTMLCanvasElement).getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  handlePointerDown = (e: any) => {
    this.isDown = true;
    this.resizeMode = this.determineModeFromPointerEvent(e);
    this.dragStartXInside = e.data.getLocalPosition(e.currentTarget).x;
    this.dragEndXInside = this.props.width - this.dragStartXInside;

    const startPosition = e.data.getLocalPosition(e.currentTarget.parent);
    this.dragStartStageX = startPosition.x;
    this.dragStartStageY = startPosition.y;
    this.attachGlobalPointerListeners();

    if (this.props.onSelect) {
      this.props.onSelect(this.props.itemId);
    }
  }

  handlePointerMove = (e: any) => {
    if (!this.isOver || this.isDown || this.isDragging) {
      return;
    }

    const mode = this.determineModeFromPointerEvent(e);

    if (mode === 'left') {
      e.currentTarget.cursor = 'resizeLeft';
    }
    else if (mode === 'right') {
      e.currentTarget.cursor = 'resizeRight';
    }
    else {
      e.currentTarget.cursor = null;
    }
  }

  handleGlobalPointerMove = (e: PointerEvent | MouseEvent) => {
    if (!this.isDown) {
      return;
    }

    const stagePosition = this.getStagePositionFromGlobalPointerEvent(e);
    if (!this.isDragging) {
      const deltaX = stagePosition.x - this.dragStartStageX;
      const deltaY = stagePosition.y - this.dragStartStageY;
      if (Math.abs(deltaX) < 3 && Math.abs(deltaY) < 3) {
        return;
      }
      this.isDragging = true;
    }

    if (this.resizeMode === 'left') {
      this.props.onMove(this.props.itemId, stagePosition.x - this.dragStartXInside, 'start');
    }
    else if (this.resizeMode === 'right') {
      this.props.onMove(this.props.itemId, stagePosition.x + this.dragEndXInside, 'end');
    }
    else {
      this.props.onMove(this.props.itemId, stagePosition.x - this.dragStartXInside, 'move');
    }
  }

  handleGlobalPointerUp = () => {
    if (!this.isDown) {
      return;
    }

    this.handlePointerUpOutside(null);
  }

  handlePointerOver = () => {
    this.isOver = true;
  }

  handlePointerOut = () => {
    this.isOver = false;
  }

  handlePointerUp = (e: any) => {
    if (this.isDown && !this.isDragging) {
      const now = Date.now();
      const wasDoubleClick = this.props.onDoubleClick && (now - this.lastPointerUpAt) < 300;

      if (wasDoubleClick) {
        this.lastPointerUpAt = 0;
        this.props.onDoubleClick!(this.props.itemId);
      }
      else {
        this.lastPointerUpAt = now;
        if (this.props.onClick) {
          this.props.onClick(this.props.itemId);
        }
      }
    }

    this.handlePointerUpOutside(e);
  }

  handlePointerUpOutside  = (_e: any) => {
    if (this.isDragging) {
      this.props.onMoveDone(this.props.itemId);
    }
    this.detachGlobalPointerListeners();
    this.isDragging = false;
    this.isDown = false;
  }
})


type LinkedBoundaryMarkerProps = {
  x: number,
  itemId: any,
  onMove: (wordId: any, time: number, mode: 'start'|'end'|'move') => void,
  onMoveDone: (wordId: any) => void,
  onDoubleClick: (wordId: any) => void,
  y?: number,
  app: PIXI.Application,
}

export const LinkedBoundaryMarker = withPixiApp(
  // @ts-ignore
  class LinkedBoundaryMarker extends React.PureComponent<LinkedBoundaryMarkerProps> {
    componentDidMount() {
      this.props.app.renderer.plugins.interaction.cursorStyles['linkedBoundary'] = 'col-resize';
    }

    componentWillUnmount() {
      this.detachGlobalPointerListeners();
    }

    render() {
      const dashes: React.ReactNode[] = [];
      for (let y=0; y < 20; y += 5) {
        dashes.push(<Rectangle
          key={y}
          fill={string2hex('#fb8c00')}
          borderColor={string2hex('#fb8c00')}
          x={0}
          y={y}
          width={2}
          height={3}
        />);
      }

      return <Container
        x={this.props.x - 1}
        y={this.props.y ?? 30}
        interactive={true}
        pointerdown={this.handlePointerDown}
        pointermove={this.handlePointerMove}
        pointerover={this.handlePointerOver}
        pointerout={this.handlePointerOut}
        pointerup={this.handlePointerUp}
        pointerupoutside={this.handlePointerUpOutside}
      >
        {dashes}
      </Container>
    }

    private isDown: boolean = false;
    private isDragging: boolean = false;
    private lastPointerUpAt: number = 0;
    private dragStartStageX: number = 0;
    private dragStartStageY: number = 0;
    private hasGlobalPointerListeners: boolean = false;

    attachGlobalPointerListeners() {
      if (this.hasGlobalPointerListeners) {
        return;
      }

      window.addEventListener('pointermove', this.handleGlobalPointerMove);
      window.addEventListener('pointerup', this.handleGlobalPointerUp);
      window.addEventListener('pointercancel', this.handleGlobalPointerUp);
      window.addEventListener('mousemove', this.handleGlobalPointerMove);
      window.addEventListener('mouseup', this.handleGlobalPointerUp);
      this.hasGlobalPointerListeners = true;
    }

    detachGlobalPointerListeners() {
      if (!this.hasGlobalPointerListeners) {
        return;
      }

      window.removeEventListener('pointermove', this.handleGlobalPointerMove);
      window.removeEventListener('pointerup', this.handleGlobalPointerUp);
      window.removeEventListener('pointercancel', this.handleGlobalPointerUp);
      window.removeEventListener('mousemove', this.handleGlobalPointerMove);
      window.removeEventListener('mouseup', this.handleGlobalPointerUp);
      this.hasGlobalPointerListeners = false;
    }

    getStagePositionFromGlobalPointerEvent(e: PointerEvent | MouseEvent) {
      const rect = (this.props.app.view as HTMLCanvasElement).getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }

    handlePointerDown = (e: any) => {
      this.isDown = true;
      e.currentTarget.cursor = 'linkedBoundary';

      const startPosition = e.data.getLocalPosition(e.currentTarget.parent);
      this.dragStartStageX = startPosition.x;
      this.dragStartStageY = startPosition.y;
      this.attachGlobalPointerListeners();
    }

    handlePointerMove = (e: any) => {
      e.currentTarget.cursor = 'linkedBoundary';
    }

    handleGlobalPointerMove = (e: PointerEvent | MouseEvent) => {
      if (!this.isDown) {
        return;
      }

      const stagePosition = this.getStagePositionFromGlobalPointerEvent(e);
      if (!this.isDragging) {
        const deltaX = stagePosition.x - this.dragStartStageX;
        const deltaY = stagePosition.y - this.dragStartStageY;
        if (Math.abs(deltaX) < 3 && Math.abs(deltaY) < 3) {
          return;
        }
        this.isDragging = true;
      }

      this.props.onMove(this.props.itemId, stagePosition.x, 'end');
    }

    handleGlobalPointerUp = () => {
      if (!this.isDown) {
        return;
      }

      this.handlePointerUpOutside();
    }

    handlePointerOver = (e: any) => {
      e.currentTarget.cursor = 'linkedBoundary';
    }

    handlePointerOut = (e: any) => {
      e.currentTarget.cursor = null;
    }

    handlePointerUp = () => {
      if (this.isDown && !this.isDragging) {
        const now = Date.now();
        const wasDoubleClick = (now - this.lastPointerUpAt) < 300;

        if (wasDoubleClick) {
          this.lastPointerUpAt = 0;
          this.props.onDoubleClick(this.props.itemId);
        }
        else {
          this.lastPointerUpAt = now;
        }
      }

      this.handlePointerUpOutside();
    }

    handlePointerUpOutside = () => {
      if (this.isDragging) {
        this.props.onMoveDone(this.props.itemId);
      }
      this.detachGlobalPointerListeners();
      this.isDragging = false;
      this.isDown = false;
    }
  }
)


function* getVisibleLines(lines: CaptionTrackRoot, visibleFrom: number, visibleTo: number) {
  for (let i=0; i <= lines.length-1; i++) {
    const line = lines[i];
    const {time, duration} = line.data;
    if (isEmpty(time) || isEmpty(duration)) {
      continue;
    }
    const lineEnd = time + duration;

    const isVisible = (
      (time >= visibleFrom && lineEnd <= visibleTo) ||
      (time <= visibleFrom && lineEnd > visibleFrom) ||
      (time < visibleTo && lineEnd >= visibleTo)
    );

    if (isVisible) {
      yield {
        index: i,
        line: line,
        time: time,
        duration: duration
      }
    }
  }
}
